/** SUNTRONIX ONE — ONE APPROVALS inbox (universal engine, /api/approvals).
 *  MY ONE → Approvals. Views + actions + detail with audit trail + ONE AI.
 *  Mobile-first: approve/reject/compare without a desktop.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  CheckCircle2, XCircle, Clock, ArrowUpCircle, Inbox as InboxIcon, Send,
  ShieldCheck, Sparkles, Loader2, MessageSquare, AlertTriangle, History,
} from "lucide-react";

const VIEWS = [
  { k: "awaiting_me", l: "Awaiting my approval", icon: InboxIcon },
  { k: "submitted_by_me", l: "Submitted by me", icon: Send },
  { k: "approved", l: "Approved", icon: CheckCircle2 },
  { k: "rejected", l: "Rejected", icon: XCircle },
  { k: "escalated", l: "Escalated", icon: ArrowUpCircle },
  { k: "overdue", l: "Overdue", icon: Clock },
];
const STATUS_TONE = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  ESCALATED: "bg-orange-100 text-orange-800 border-orange-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  AUTO_APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
  CHANGES_REQUESTED: "bg-sky-100 text-sky-800 border-sky-200",
  SUPERSEDED: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function OneApprovals() {
  const [view, setView] = useState("awaiting_me");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/approvals/inbox", { params: { view } })
      .then(({ data }) => { setItems(data.items || []); setCounts(data.counts || {}); })
      .catch(() => toast.error("Could not load approvals"))
      .finally(() => setLoading(false));
  }, [view]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="one-approvals">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-900 text-white grid place-items-center"><ShieldCheck className="h-6 w-6" strokeWidth={1.6} /></div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">ONE Approvals</h1>
          <p className="text-sm text-slate-500 mt-0.5">One approval engine across every SUNTRONIX ONE workflow.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-slate-200">
        {VIEWS.map((v) => {
          const Icon = v.icon; const n = counts[v.k] || 0;
          return (
            <button key={v.k} data-testid={`approvals-view-${v.k}`} onClick={() => setView(v.k)}
              className={`px-3 py-2 text-[13px] font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${view === v.k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="h-4 w-4" /> {v.l}
              {n > 0 && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${view === v.k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-slate-400 border border-dashed border-slate-200 rounded-xl" data-testid="approvals-empty">Nothing here.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="approvals-list">
          {items.map((r) => (
            <button key={r.id} data-testid={`approval-card-${r.id}`} onClick={() => setSel(r)}
              className="text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-slate-300 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.entity_type.replace(/_/g, " ")} · by {r.submitted_by_email}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge className={`text-[9px] border ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status.replace(/_/g, " ")}</Badge>
                  {r.is_overdue && <span className="text-[9px] text-rose-600 font-semibold inline-flex items-center gap-0.5"><AlertTriangle className="h-2.5 w-2.5" /> OVERDUE</span>}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                {Object.entries(r.context || {}).slice(0, 4).map(([k, v]) => (
                  <span key={k} className="px-1.5 py-0.5 rounded bg-slate-50 border border-slate-100 text-slate-600">{k.replace(/_/g, " ")}: <b>{String(v)}</b></span>
                ))}
              </div>
              {r.can_act && <div className="mt-2 text-[10px] font-semibold text-amber-700">Awaiting your decision →</div>}
            </button>
          ))}
        </div>
      )}

      <ApprovalDrawer req={sel} onClose={() => setSel(null)} onChanged={() => { load(); setSel(null); }} />
    </div>
  );
}

function ApprovalDrawer({ req, onClose, onChanged }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [full, setFull] = useState(null);

  useEffect(() => {
    setComment(""); setAi(null); setFull(null);
    if (req?.id) api.get(`/approvals/${req.id}`).then(({ data }) => setFull(data)).catch(() => setFull(req));
  }, [req]);

  if (!req) return null;
  const r = full || req;
  const doAction = async (action, extra = {}) => {
    if ((action === "reject" || action === "request_change") && !comment.trim()) { toast.error("Please add a comment"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/approvals/${r.id}/action`, { action, comment: comment.trim() || undefined, ...extra });
      toast.success(`Marked ${data.status.replace(/_/g, " ").toLowerCase()}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const explain = async (mode) => {
    setAi("loading");
    try { const { data } = await api.get(`/approvals/${r.id}/explain`, { params: { mode } }); setAi(data); }
    catch { setAi(null); toast.error("Explain failed"); }
  };

  return (
    <Sheet open={!!req} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="approval-drawer">
        <SheetHeader><SheetTitle className="text-[16px]">{r.title}</SheetTitle></SheetHeader>
        <div className="mt-3 space-y-4 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`border ${STATUS_TONE[r.status] || "bg-slate-100"}`}>{r.status.replace(/_/g, " ")}</Badge>
            <span className="text-slate-500">{r.entity_type.replace(/_/g, " ")} · {r.entity_id}</span>
          </div>
          <div className="text-[12px] text-slate-500">Submitted by {r.submitted_by_email} · {r.submitted_at?.slice(0, 16).replace("T", " ")}</div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Context</div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(r.context || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2"><span className="text-slate-500">{k.replace(/_/g, " ")}</span><b className="text-slate-800">{String(v)}</b></div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Approval levels · {(r.rule_names || []).join(", ") || "no rule"}</div>
            {(r.levels || []).map((lv) => (
              <div key={lv.level} className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0">
                <span className="text-slate-700">L{lv.level} · {lv.mode.replace(/_/g, " ")} · {(lv.approver_roles || []).join(", ")}</span>
                <Badge className={`text-[9px] border ${STATUS_TONE[lv.status] || "bg-slate-100"}`}>{lv.status}</Badge>
              </div>
            ))}
            {(r.levels || []).length === 0 && <div className="text-slate-400 text-[12px]">Auto-approved (no rule triggered).</div>}
          </div>

          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="h-4 w-4 text-emerald-600" />
              <span className="text-[12px] font-semibold text-slate-800">ONE AI</span>
              <div className="ml-auto flex gap-1.5">
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => explain("why")} data-testid="approval-ai-why">Why?</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => explain("summary")} data-testid="approval-ai-summary">Summarise</Button>
              </div>
            </div>
            {ai === "loading" ? <div className="text-[12px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" /> Thinking…</div>
              : ai ? <div className="text-[12px] text-slate-700" data-testid="approval-ai-answer">{ai.ai_explanation || ai.deterministic}</div>
              : <div className="text-[11px] text-slate-400">Ask why this needs approval, or get a summary.</div>}
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1"><History className="h-3 w-3" /> Audit trail</div>
            {(r.audit || []).map((a, i) => (
              <div key={i} className="text-[11.5px] text-slate-600 py-0.5">
                <b>{a.action}</b>{a.by ? ` · ${a.by}` : ""}{a.comment ? ` — "${a.comment}"` : ""} <span className="text-slate-400">{a.at?.slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>

          {r.can_act && (
            <div className="space-y-2 sticky bottom-0 bg-white pt-2 border-t border-slate-100">
              <Textarea data-testid="approval-comment" placeholder="Add a comment (required to reject / request change)…" value={comment} onChange={(e) => setComment(e.target.value)} className="min-h-[64px]" />
              <div className="grid grid-cols-2 gap-2">
                <Button disabled={busy} onClick={() => doAction("approve")} data-testid="approval-approve" className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
                <Button disabled={busy} onClick={() => doAction("reject")} data-testid="approval-reject" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                <Button disabled={busy} onClick={() => doAction("request_change")} data-testid="approval-request-change" variant="outline" size="sm"><MessageSquare className="h-3.5 w-3.5 mr-1" /> Request change</Button>
                <Button disabled={busy} onClick={() => doAction("escalate")} data-testid="approval-escalate" variant="outline" size="sm"><ArrowUpCircle className="h-3.5 w-3.5 mr-1" /> Escalate</Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
